import clsx from 'clsx';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useAuthContext } from '../../useAuthContext';
import { toAbsoluteUrl } from '@/utils';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { FormattedMessage } from 'react-intl';
import FormInput from '@/components/ui/input/FormInput';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import Turnstile from 'react-turnstile';
import BtnSubmitAuth from '../../../components/ui/btnSubmitAuth';

const initialValues = {
  fullname: '',
  email: '',
  password: '',
  changepassword: '',
  acceptTerms: false
};

const Signup = () => {

  const intl = useIntl();
  const signupSchema = Yup.object().shape({
    fullname: Yup.string()
      .min(3, intl.formatMessage({ id: 'AUTH.ERROR.MIN_LENGTH_NAME' }, { min: 3 }))
      .max(50, intl.formatMessage({ id: 'AUTH.ERROR.MAX_LENGTH_NAME' }, { max: 50 }))
      .required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_NAME' })),
    email: Yup.string()
    .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/ , intl.formatMessage({ id: 'AUTH.ERROR.INVALID_EMAIL' }))
    .min(6, intl.formatMessage({ id: 'AUTH.ERROR.MIN_LENGTH_EMAIL' }))
    .max(50, intl.formatMessage({ id: 'AUTH.ERROR.MAX_LENGTH_EMAIL' }))
    .required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_EMAIL' })),
    password: Yup.string()
    .min(6, intl.formatMessage({ id: 'AUTH.ERROR.MIN_LENGTH_PASSWORD' }))
    .max(50, intl.formatMessage({ id: 'AUTH.ERROR.MAX_LENGTH_PASSWORD' }))
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])/, intl.formatMessage({ id: 'AUTH.ERROR.PASSWORD_COMPLEXITY' }))
    .required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_PASSWORD' })),
    changepassword: Yup.string()
    .min(6, intl.formatMessage({ id: 'AUTH.ERROR.MIN_LENGTH_PASSWORD' }))
    .max(50, intl.formatMessage({ id: 'AUTH.ERROR.MAX_LENGTH_PASSWORD' }))
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])/, intl.formatMessage({ id: 'AUTH.ERROR.PASSWORD_COMPLEXITY' }))
    .required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_PASSWORD' }))
    .oneOf([Yup.ref('password')], intl.formatMessage({ id: 'AUTH.ERROR.PASSWORD_MISMATCH' })),
    acceptTerms: Yup.bool().required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_TERMS' }))
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const { currentLayout } = useLayout();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const resetCaptchaWidget = () => {
    setCaptcha('');
    setCaptchaKey((k) => k + 1);
  };
  const formik = useFormik({
    initialValues,
    validationSchema: signupSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setStatus(undefined);
      setSubmitting(false);
      // Nếu chưa có captcha, luôn hiển thị và refresh captcha để buộc xác thực mới
      if (!captcha) {
        setShowCaptcha(true);
        resetCaptchaWidget();
        return;
      }
      setLoading(true);
      try {
        if (!register) {
          throw new Error('JWTProvider is required for this form.');
        }
        await register(
          values.fullname,
          values.email,
          values.password,
          values.changepassword,
          captcha
        );
        toast.success(intl.formatMessage({ id: 'AUTH.SIGNUP.SUCCESS' }));
        navigate(`/auth/check-email?email=${encodeURIComponent(values.email)}`, {
          replace: true
        });
      } catch (error) {
        console.error(error);
        let errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.SIGNUP_FAILED' });
        
        // Handle Axios network/timeout errors without response
        if (
          (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error')) ||
          error?.code === 'ECONNABORTED'
        ) {
          errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.NETWORK' });
        } else if (error.response) {
          // Handle different error status codes
          const { status, data: responseData } = error.response;
          
          switch (status) {
            case 400:
              if (responseData?.error === 'CAPTCHA token missing') {
                errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.CAPTCHA_MISSING' });
              } else if (responseData?.error === 'CAPTCHA verification failed') {
                errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.CAPTCHA_FAILED' });
              } else if (responseData?.error === 'Invalid referrer code') {
                errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.INVALID_REFERRER' });
              } else {
                errorMessage = responseData?.error || errorMessage;
              }
              break;
            case 422:
              // Validation errors
              if (responseData?.errors) {
                const firstError = Object.values(responseData.errors)[0];
                if (Array.isArray(firstError)) {
                  errorMessage = firstError[0];
                } else {
                  errorMessage = firstError;
                }
              } else if (responseData?.error) {
                errorMessage = responseData.error;
              } else {
                errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.VALIDATION_FAILED' });
              }
              break;
            case 429:
              errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.TOO_MANY_REQUESTS' });
              break;
            case 500:
              errorMessage = intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' });
              break;
            default:
              if (responseData?.error) {
                errorMessage = responseData.error;
              } else if (error.message) {
                errorMessage = error.message;
              }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setStatus(errorMessage);
        setSubmitting(false);
      }
      setLoading(false);
      // Sau mỗi lần nhấn đăng ký, reset captcha để lần tiếp theo phải xác thực lại
      resetCaptchaWidget();
      setShowCaptcha(false);
    }
  });

  useEffect(() => {
    if (captcha && showCaptcha) {
      formik.handleSubmit();
    }
  }, [captcha]);

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  return <div className="card max-w-[370px] w-full">
    <form className="card-body flex flex-col gap-5 p-10" noValidate onSubmit={formik.handleSubmit}>
      <div className="text-center mb-2.5">
        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5"><FormattedMessage id="AUTH.SIGNUP.TITLE" /></h3>
        <div className="flex items-center justify-center font-medium">
          <span className="text-2sm text-gray-600 me-1.5"><FormattedMessage id="AUTH.SIGNUP.ALREADY_HAVE_ACCOUNT" /></span>
          <Link to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'} className="text-2sm link text-[#50B553]">
            <FormattedMessage id="AUTH.SIGNUP.SIGN_IN" />
          </Link>
        </div>
      </div>

      {/* <div className="grid grid-cols-2 gap-2.5">
        <a href="#" className="btn btn-light btn-sm justify-center">
          <img src={toAbsoluteUrl('/media/brand-logos/google.svg')} className="size-3.5 shrink-0" />
          <FormattedMessage id="AUTH.SIGNUP.USE_GOOGLE" />
        </a>

        <a href="#" className="btn btn-light btn-sm justify-center">
          <img src={toAbsoluteUrl('/media/brand-logos/apple-black.svg')} className="size-3.5 shrink-0 dark:hidden" />
          <img src={toAbsoluteUrl('/media/brand-logos/apple-white.svg')} className="size-3.5 shrink-0 light:hidden" />
          <FormattedMessage id="AUTH.SIGNUP.USE_APPLE" />
        </a>
      </div> */}

      <div className="flex items-center gap-2">
        <span className="border-t border-gray-200 w-full"></span>
        <span className="text-2xs text-gray-500 font-medium uppercase"><FormattedMessage id="AUTH.SIGNUP.OR" /></span>
        <span className="border-t border-gray-200 w-full"></span>
      </div>

      <FormInput
        label={intl.formatMessage({ id: 'AUTH.FULLNAME' })}
        name="fullname"
        type="text"
        icon={<KeenIcon icon="user-square" className="text-lg" />}
        placeholder={intl.formatMessage({ id: 'AUTH.SIGNUP.FULLNAME_PLACEHOLDER' })}
        autoComplete="name"
        error={formik.errors.fullname}
        touched={formik.touched.fullname}
        required
        {...formik.getFieldProps('fullname')}
      />

      <FormInput
        label={intl.formatMessage({ id: 'AUTH.EMAIL' })}
        name="email"
        type="email"
        icon={<KeenIcon icon="sms" className="text-lg" />}
        placeholder="example@email.com"
        autoComplete="new-email"
        error={formik.errors.email}
        touched={formik.touched.email}
        required
        {...formik.getFieldProps('email')}
      />

      <FormInput
        label={intl.formatMessage({ id: 'AUTH.PASSWORD' })}
        name="password"
        type="password"
        icon={<KeenIcon icon="lock-2" className="text-lg" />}
        placeholder={intl.formatMessage({ id: 'AUTH.SIGNUP.PASSWORD_PLACEHOLDER' })}
        autoComplete="new-password"
        error={formik.errors.password}
        touched={formik.touched.password}
        required
        {...formik.getFieldProps('password')}
      />

      <FormInput
        label={intl.formatMessage({ id: 'AUTH.SIGNUP.CONFIRM_PASSWORD' })}
        name="changepassword"
        type="password"
        icon={<KeenIcon icon="key" className="text-lg" />}
        placeholder={intl.formatMessage({ id: 'AUTH.SIGNUP.CONFIRM_PASSWORD_PLACEHOLDER' })}
        autoComplete="off"
        error={formik.errors.changepassword}
        touched={formik.touched.changepassword}
        required
        {...formik.getFieldProps('changepassword')}
      />

      <label className="checkbox-group">
        <input 
          className="checkbox checkbox-sm" 
          type="checkbox" 
          {...formik.getFieldProps('acceptTerms')} 
        />
        <span className="checkbox-label">
          <FormattedMessage id="AUTH.SIGNUP.ACCEPT" />{' '}
          <Link to="/terms-and-conditions" className="text-2sm link text-[#50B553]">
            <FormattedMessage id="AUTH.SIGNUP.TERMS" />
          </Link>
        </span>
      </label>

      {formik.touched.acceptTerms && formik.errors.acceptTerms && (
        <span role="alert" className="text-danger text-xs mt-1">
          {formik.errors.acceptTerms}
        </span>
      )}

      {/* Error message display */}
      {formik.status && (
        <div className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {formik.status}
        </div>
      )}

      {showCaptcha && (
        <Turnstile
          key={captchaKey}
         
          sitekey={recaptchaSiteKey}
          onSuccess={token => setCaptcha(token)}
        />
      )}

      <BtnSubmitAuth
          loading={loading || formik.isSubmitting}
          disabled={loading || formik.isSubmitting || !formik.values.acceptTerms}
        >
          {loading || formik.isSubmitting ? (
            <FormattedMessage id="AUTH.PLEASE_WAIT" />
          ) : (
            <FormattedMessage id="AUTH.SIGNUP.SUBMIT" />
          )}
        </BtnSubmitAuth>
    </form>
  </div>;
};
export { Signup };